/**
 * StatcastAgentPanel - Direct StatCast Text-to-SQL with Multi-Agent Analysis
 *
 * A focused interface for querying Statcast data with natural language.
 * Shows the complete agentic process: intent → model → SQL → execute → results
 *
 * Features:
 * - Viewport-constrained layout with internal scrolling
 * - Prominent AI response section above technical details
 * - Collapsible sections for SQL and raw data
 * - Multi-agent integration for strategic recommendations
 *
 * No contextual layers - just direct StatCast data access.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { ChevronDown, ChevronUp, Sparkles, Database, BarChart3, MessageSquare, Brain } from 'lucide-react';
import { marked } from 'marked';
import AgentTraceView from './AgentTraceView';
import StatcastChartRenderer from './StatcastChartRenderer';
import VideoUploadInline from './VideoUploadInline';

// Example queries for user guidance
const EXAMPLE_QUERIES = [
  "Show me the top 10 fastest fastballs this season",
  "What's the average spin rate for sliders?",
  "Compare Shohei Ohtani and Gerrit Cole's velocity",
  "Find pitchers with the best sweeper movement",
  "Who has the highest release extension?",
];

// Generate unique step ID
const generateStepId = () => `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// ADK Multi-Agent: Extract pitcher name from query
const extractPitcherName = (query) => {
  // Common patterns for pitcher names in queries
  const patterns = [
    /(?:analyze|for|of|about|show me)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i,
    /([A-Z][a-z]+\s+[A-Z][a-z]+)'s?\s+(?:video|pitch|tipping|mechanics)/i,
    /pitcher\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/i,
  ];

  for (const pattern of patterns) {
    const match = query.match(pattern);
    if (match) return match[1];
  }
  return null;
};

export default function StatcastAgentPanel({ showTrace = true }) {
  const [query, setQuery] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedSql, setStreamedSql] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [usedModel, setUsedModel] = useState('');

  // Agentic trace state
  const [traceSteps, setTraceSteps] = useState([]);

  // ADK Multi-Agent: Chart state
  const [chartSpecs, setChartSpecs] = useState([]);
  const [toolCalls, setToolCalls] = useState([]);

  // ADK Multi-Agent: Video upload state
  const [uploadedVideo, setUploadedVideo] = useState(null);

  // Multi-Agent AI Response state
  const [aiResponse, setAiResponse] = useState('');
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);

  // Collapsible state
  const [sqlOpen, setSqlOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);

  const abortControllerRef = useRef(null);
  const sqlStepIdRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Auto-scroll within container when new content arrives (NOT page scroll)
  useEffect(() => {
    if (scrollContainerRef.current && (traceSteps.length > 0 || results || chartSpecs.length > 0 || aiResponse)) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [traceSteps, results, chartSpecs, aiResponse]);

  // DEBUG: Log render conditions for charts
  useEffect(() => {
    console.log('[StatcastAgentPanel] Render check - chartSpecs:', chartSpecs.length, 'results?.data:', results?.data?.length, 'aiResponse length:', aiResponse?.length);
    if (chartSpecs.length > 0) {
      console.log('[StatcastAgentPanel] Chart specs available:', chartSpecs);
    }
    if (results?.data) {
      console.log('[StatcastAgentPanel] Results data available:', results.data.length, 'rows');
    }
  }, [chartSpecs, results, aiResponse]);

  // Helper: Generate unique chart ID for deduplication
  const getChartId = useCallback((spec) => {
    return `${spec.type}-${spec.title}-${spec.x}-${spec.y}`;
  }, []);

  // Helper: Add chart spec with deduplication
  const addChartSpec = useCallback((newSpec) => {
    setChartSpecs(prev => {
      const newId = getChartId(newSpec);
      const exists = prev.some(s => getChartId(s) === newId);
      if (exists) {
        console.log('[StatcastAgentPanel] Skipping duplicate chart:', newSpec.title);
        return prev;
      }
      console.log('[StatcastAgentPanel] Adding unique chart:', newSpec.title);
      return [...prev, newSpec];
    });
  }, [getChartId]);

  // Add a new step to the trace
  const addTraceStep = useCallback((step) => {
    const newStep = {
      id: generateStepId(),
      timestamp: Date.now(),
      ...step
    };
    setTraceSteps(prev => [...prev, newStep]);
    return newStep.id;
  }, []);

  // Update an existing step
  const updateTraceStep = useCallback((stepId, updates) => {
    setTraceSteps(prev =>
      prev.map(step =>
        step.id === stepId ? { ...step, ...updates } : step
      )
    );
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!query.trim() || isStreaming) return;

    // Reset state
    setIsStreaming(true);
    setStreamedSql('');
    setResults(null);
    setError(null);
    setUsedModel('');
    setTraceSteps([]);
    setChartSpecs([]);
    setToolCalls([]);
    setAiResponse('');
    setSqlOpen(false);
    setDataOpen(false);
    sqlStepIdRef.current = null;

    // Add user query step
    addTraceStep({
      type: 'user',
      label: query.trim(),
      content: query.trim(),
    });

    // Create abort controller for cleanup
    abortControllerRef.current = new AbortController();

    const startTime = Date.now();

    try {
      // Use vanilla endpoint for clean StatCast access (no contextual layers)
      // ADK Multi-Agent: Include video URI if uploaded
      const requestBody = {
        query: query.trim(),
        ...(uploadedVideo && {
          videoUri: uploadedVideo.uri,
          pitcherName: extractPitcherName(query.trim()),
        }),
      };

      const response = await fetch('/api/chat/statcast/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentSql = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'thinking':
                  addTraceStep({
                    type: 'thinking',
                    label: data.text,
                    text: data.text,
                  });
                  break;

                case 'model':
                  addTraceStep({
                    type: 'model',
                    label: `Selected model: ${data.text}`,
                    model: data.model || data.text.split(': ').pop(),
                    text: data.text,
                  });
                  break;

                // ADK Multi-Agent: New event types
                case 'reasoning':
                  addTraceStep({
                    type: 'reasoning',
                    label: data.text,
                    text: data.text,
                  });
                  break;

                case 'tool_call':
                  addTraceStep({
                    type: 'tool_call',
                    label: `Calling ${data.tool}...`,
                    tool: data.tool,
                    params: data.params,
                    streaming: data.status === 'started',
                  });
                  break;

                case 'tool_result':
                  addTraceStep({
                    type: 'tool_result',
                    label: `${data.tool} ${data.status === 'success' ? 'completed' : 'failed'}`,
                    tool: data.tool,
                    duration: data.duration,
                    hasData: data.hasData,
                  });
                  break;

                case 'chart':
                  console.log('[StatcastAgentPanel] Chart event received:', data);
                  addTraceStep({
                    type: 'chart',
                    label: `Generated ${data.spec?.type} chart: ${data.spec?.title}`,
                    chartSpec: data.spec,
                  });
                  // ADK Multi-Agent: Store chart spec with deduplication
                  if (data.spec) {
                    const normalizedSpec = {
                      ...data.spec,
                      x: data.spec.x || data.spec.xAxis,
                      y: data.spec.y || data.spec.yAxis,
                    };
                    addChartSpec(normalizedSpec);
                  } else {
                    console.warn('[StatcastAgentPanel] Chart event has no spec:', data);
                  }
                  break;

                case 'generating':
                  // Create SQL generation step
                  sqlStepIdRef.current = addTraceStep({
                    type: 'sql',
                    label: 'Generating SQL query...',
                    sql: '',
                    streaming: true,
                  });
                  break;

                case 'sql_chunk':
                  currentSql += data.text;
                  setStreamedSql(currentSql);
                  // Update SQL step with new content
                  if (sqlStepIdRef.current) {
                    updateTraceStep(sqlStepIdRef.current, {
                      sql: currentSql,
                    });
                  }
                  break;

                case 'sql_complete':
                  // Mark SQL generation complete
                  if (sqlStepIdRef.current) {
                    updateTraceStep(sqlStepIdRef.current, {
                      streaming: false,
                      label: 'SQL query generated',
                      duration: Date.now() - startTime,
                    });
                  }
                  break;

                case 'executing':
                  addTraceStep({
                    type: 'execute',
                    label: data.text || 'Executing query on BigQuery...',
                    text: data.text,
                  });
                  break;

                case 'results':
                  console.log('[StatcastAgentPanel] Results event received:', { count: data.count, dataLength: data.data?.length, data: data.data });
                  setResults(data);
                  addTraceStep({
                    type: 'results',
                    label: `Found ${data.count} results`,
                    data: data.data,
                    output: `${data.count} rows returned`,
                    duration: Date.now() - startTime,
                  });
                  // AI analysis is now streamed inline via 'analysis' events from backend
                  break;

                // NEW: Handle AI analysis events from backend
                case 'analysis_start':
                  setIsGeneratingResponse(true);
                  addTraceStep({
                    type: 'analysis',
                    label: 'Generating AI analysis...',
                    streaming: true,
                  });
                  break;

                case 'analysis_chunk':
                  setAiResponse(prev => prev + (data.delta || data.text || ''));
                  break;

                case 'analysis_complete':
                  setIsGeneratingResponse(false);
                  addTraceStep({
                    type: 'analysis_done',
                    label: 'AI analysis complete',
                    duration: data.duration,
                  });
                  break;

                case 'done':
                  setUsedModel(data.model);
                  // ADK Multi-Agent: Store tool calls (charts already added via 'chart' events)
                  if (data.toolCalls) {
                    setToolCalls(data.toolCalls);
                  }
                  // Note: Charts are now deduplicated via addChartSpec, but done event
                  // chart specs are redundant - they're already sent via 'chart' events
                  break;

                case 'error':
                  setError(data.message);
                  addTraceStep({
                    type: 'error',
                    label: 'Error occurred',
                    content: data.message,
                  });
                  break;
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE message:', line);
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to connect to Statcast');
        addTraceStep({
          type: 'error',
          label: 'Connection error',
          content: err.message || 'Failed to connect to Statcast',
        });
      }
    } finally {
      setIsStreaming(false);
    }
  }, [query, isStreaming, addTraceStep, updateTraceStep, addChartSpec]);

  const handleExampleClick = useCallback((example) => {
    setQuery(example);
    setTimeout(() => {
      const form = document.getElementById('statcast-agent-form');
      if (form) form.requestSubmit();
    }, 100);
  }, []);

  const clearResults = useCallback(() => {
    setQuery('');
    setStreamedSql('');
    setResults(null);
    setError(null);
    setUsedModel('');
    setTraceSteps([]);
    setChartSpecs([]);
    setToolCalls([]);
    setAiResponse('');
    setSqlOpen(false);
    setDataOpen(false);
  }, []);

  return (
    <div className="w-full max-w-7xl mx-auto flex flex-col h-[calc(100vh-120px)] max-h-[900px]">
      {/* Header Card - Dark Glassmorphism - Fixed */}
      <div
        className="rounded-t-2xl flex-shrink-0"
        style={{
          background: 'rgba(13, 13, 18, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderBottom: 'none',
        }}
      >
        <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="text-xl">⚾</span>
                StatCast Direct Query
                <Badge className="ml-2 text-xs bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-400 border-indigo-500/30">
                  Multi-Agent AI
                </Badge>
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Ask questions about MLB pitch data. Get strategic recommendations powered by AI agents.
              </p>
            </div>
            {usedModel && (
              <Badge className="text-xs bg-white/5 text-gray-300 border-white/20">
                {usedModel.replace('-preview', '')}
              </Badge>
            )}
          </div>
        </div>

        <div className="px-6 py-3">
          {/* Query Input */}
          <form id="statcast-agent-form" onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={uploadedVideo
                ? `Analyze ${uploadedVideo.name.substring(0, 20)}... for pitch tipping`
                : "e.g., How should Guerrero approach Crochet?"
              }
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-yellow-500/50 focus:ring-yellow-500/20 h-10"
              disabled={isStreaming}
              aria-label="Enter your Statcast query"
            />
            <VideoUploadInline
              compact
              disabled={isStreaming}
              onUploadComplete={(video) => {
                setUploadedVideo(video);
                if (!query.trim()) {
                  setQuery('Analyze this video for pitch tipping patterns');
                }
              }}
              onUploadError={(error) => {
                console.error('Video upload error:', error);
              }}
            />
            <button
              type="submit"
              disabled={isStreaming || !query.trim()}
              className="px-4 py-2 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              style={{
                background: isStreaming
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'linear-gradient(135deg, #FFD700, #FFC000)',
                color: isStreaming ? '#999' : 'black',
                boxShadow: isStreaming ? 'none' : '0 0 15px rgba(255, 215, 0, 0.3)'
              }}
            >
              {isStreaming ? (
                <span className="flex items-center gap-1.5">
                  <span className="animate-spin">⚾</span>
                  Analyzing...
                </span>
              ) : (
                '🔍 Analyze'
              )}
            </button>
            {(results || error) && (
              <button
                type="button"
                onClick={clearResults}
                className="px-3 py-2 rounded-lg text-sm transition-colors"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#9CA3AF',
                }}
              >
                Clear
              </button>
            )}
          </form>

          {/* Example Queries - Compact */}
          {!streamedSql && !results && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-xs text-gray-500 mr-1">Try:</span>
              {EXAMPLE_QUERIES.slice(0, 3).map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => handleExampleClick(example)}
                  className="text-xs px-2.5 py-1 rounded-full transition-all hover:bg-yellow-500/10 hover:border-yellow-500/30 hover:text-yellow-400"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#9CA3AF'
                  }}
                  disabled={isStreaming}
                >
                  {example.length > 40 ? example.substring(0, 40) + '...' : example}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Scrollable Container */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto rounded-b-2xl"
        style={{
          background: 'rgba(13, 13, 18, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderTop: 'none',
        }}
      >
        {(traceSteps.length > 0 || results || aiResponse) ? (
          <div className="p-4 space-y-4">
            {/* Error */}
            {error && (
              <div
                className="p-3 rounded-xl text-sm"
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F87171'
                }}
              >
                <span className="font-semibold">Error:</span> {error}
              </div>
            )}

            {/* AI RESPONSE SECTION - PROMINENT */}
            {(aiResponse || isGeneratingResponse) && (
              <div
                className="rounded-xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(168, 85, 247, 0.15))',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                }}
              >
                <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.2)' }}>
                  <Brain className="w-5 h-5 text-indigo-400" />
                  <span className="font-semibold text-white">AI Analysis & Recommendations</span>
                  {isGeneratingResponse && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-indigo-300">
                      <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                      Generating insights...
                    </span>
                  )}
                </div>
                <div className="p-4">
                  {aiResponse ? (
                    <div
                      className="prose prose-sm prose-invert max-w-none text-gray-200"
                      dangerouslySetInnerHTML={{ __html: marked.parse(aiResponse) }}
                    />
                  ) : (
                    <div className="flex items-center gap-2 text-gray-400">
                      <div className="animate-pulse flex gap-1">
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-sm">Analyzing data and generating strategic recommendations...</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Charts - Always Visible When Available */}
            {chartSpecs.length > 0 && results?.data && (
              <div
                className="rounded-xl p-4"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-semibold text-gray-200">Visualizations</span>
                  <Badge variant="outline" className="text-xs bg-white/5 text-gray-300 border-white/20">
                    {chartSpecs.length} chart{chartSpecs.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <div className="grid gap-4">
                  {chartSpecs.map((spec, idx) => (
                    <StatcastChartRenderer
                      key={idx}
                      chartSpec={spec}
                      data={results.data}
                      isDarkMode={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* COLLAPSIBLE: SQL Query */}
            {streamedSql && (
              <Collapsible open={sqlOpen} onOpenChange={setSqlOpen}>
                <div
                  className="rounded-xl overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                >
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors">
                    <CollapsibleTrigger className="flex-1 flex items-center gap-2">
                      <Database className="w-4 h-4 text-green-400" />
                      <span className="text-sm font-semibold text-gray-200">Generated SQL</span>
                      {isStreaming && !results && (
                        <span className="animate-pulse text-green-300 text-xs">generating...</span>
                      )}
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(streamedSql);
                        }}
                        className="text-xs px-2 py-1 rounded hover:bg-white/10 text-gray-400"
                      >
                        Copy
                      </button>
                      <CollapsibleTrigger>
                        {sqlOpen ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </CollapsibleTrigger>
                    </div>
                  </div>
                  <CollapsibleContent>
                    <pre
                      className="p-4 overflow-x-auto text-xs font-mono max-h-[200px] overflow-y-auto"
                      style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        borderTop: '1px solid rgba(34, 197, 94, 0.2)'
                      }}
                    >
                      <code className="text-green-400">{streamedSql}</code>
                      {isStreaming && !results && <span className="animate-pulse text-green-300">▋</span>}
                    </pre>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}

            {/* COLLAPSIBLE: Raw Data Table */}
            {results?.data && results.data.length > 0 && (
              <Collapsible open={dataOpen} onOpenChange={setDataOpen}>
                <div
                  className="rounded-xl overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                >
                  <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-semibold text-gray-200">Raw Data</span>
                      <Badge variant="outline" className="text-xs bg-white/5 text-gray-300 border-white/20">
                        {results.count} row{results.count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <span>
                      {dataOpen ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="max-h-[300px] overflow-y-auto" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <Table>
                        <TableHeader className="sticky top-0">
                          <TableRow style={{ background: 'rgba(30, 30, 40, 0.95)' }}>
                            {Object.keys(results.data[0]).map((key) => (
                              <TableHead
                                key={key}
                                className="text-xs font-bold uppercase tracking-wider py-2"
                                style={{ color: '#FFD700', background: 'transparent' }}
                              >
                                {key.replace(/_/g, ' ')}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.data.map((row, rowIdx) => (
                            <TableRow
                              key={rowIdx}
                              className="border-t border-gray-700/50 hover:bg-white/5"
                            >
                              {Object.values(row).map((value, cellIdx) => (
                                <TableCell key={cellIdx} className="text-xs py-2 text-gray-200">
                                  {typeof value === 'number'
                                    ? Number.isInteger(value)
                                      ? value.toLocaleString()
                                      : value.toFixed(2)
                                    : String(value ?? '-')}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}

            {/* Empty Results */}
            {results?.data && results.data.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-400">No results found for your query.</p>
                <p className="text-sm text-gray-500 mt-1">Try adjusting your search terms.</p>
              </div>
            )}

            {/* Agentic Trace - Collapsible */}
            {showTrace && traceSteps.length > 0 && (
              <Collapsible defaultOpen={false}>
                <div
                  className="rounded-xl overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                >
                  <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <span className="text-sm font-semibold text-gray-200">Agent Trace</span>
                      <Badge variant="outline" className="text-xs bg-white/5 text-gray-300 border-white/20">
                        {traceSteps.length} step{traceSteps.length !== 1 ? 's' : ''}
                      </Badge>
                      {isStreaming && (
                        <span className="animate-pulse text-purple-300 text-xs">processing...</span>
                      )}
                    </div>
                    <span>
                      <ChevronDown className="w-4 h-4 text-gray-400 transition-transform data-[state=open]:rotate-180" />
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <AgentTraceView steps={traceSteps} isStreaming={isStreaming} />
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}

            {/* Tool Calls Summary */}
            {toolCalls.length > 0 && (
              <div
                className="p-3 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <p className="text-xs font-semibold text-gray-400 mb-2">
                  🔧 Tools Used ({toolCalls.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {toolCalls.map((tc, idx) => (
                    <Badge
                      key={idx}
                      className={`text-xs ${tc.status === 'success'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}
                    >
                      {tc.tool} ({tc.duration}ms)
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Empty state with examples */
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center max-w-md">
              <div className="text-4xl mb-4">⚾</div>
              <h3 className="text-lg font-semibold text-gray-200 mb-2">Ready to Analyze</h3>
              <p className="text-sm text-gray-400 mb-4">
                Ask strategic questions about MLB players and get data-driven recommendations from our multi-agent AI system.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {EXAMPLE_QUERIES.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleExampleClick(example)}
                    className="text-xs px-3 py-1.5 rounded-full transition-all hover:bg-yellow-500/10 hover:border-yellow-500/30 hover:text-yellow-400"
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#9CA3AF'
                    }}
                    disabled={isStreaming}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
